import React, { useContext, useEffect, useState } from 'react';
import { UseFormMethods } from 'react-hook-form';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import { HorizonInterval } from 'src/models/Horizon';
import { LandSystem } from 'src/models/LandSystem';
import ReportFieldDto from 'src/models/reports/ReportFieldDto';
import ReportHorizonDto from 'src/models/reports/ReportHorizonDto';
import ReportTownshipDto from 'src/models/reports/ReportTownshipDto';
import ReportTractDto from 'src/models/reports/ReportTractDto';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';

interface IReportFilterLocationProps extends Partial<Pick<UseFormMethods, 'getValues' | 'setValue' | 'register'>> {
    // selectedHorizonIds: number[];
    townships: ReportTownshipDto[];
    landSystemId: LandSystem;
    selectAll: boolean;
    expandAll: boolean;
    loadingFields: boolean;
    selectedHorizons: ReportHorizonDto[];

    refreshTagValues: () => void;
    setLoadingFields: (loading: boolean) => void;
    setSelectedHorizons: (horizons: ReportHorizonDto[]) => void;
}

const ReportFilterLocation = (props: IReportFilterLocationProps) => {
    const appContext = useContext<IAppContextState>(AppContext);
    
    // Need temp statuses for cascading logic
    const [selectAll, setSelectAll] = useState<boolean>(false);
    
    const [expandedTracts, setExpandedTracts] = useState<number[]>([]);
    const [expandedFields, setExpandedFields] = useState<number[]>([]);
    const [expandedTownships, setExpandedTownships] = useState<number[]>([]);

    const [toggleSelect, setToggleSelect] = useState<boolean>(false);

    useEffect(() => {
        if (props.townships?.length > 0) {
            props.setLoadingFields(false);
            props.refreshTagValues();
            if (props.landSystemId === LandSystem.EastTexas) {
                setExpandedFields([1]);
            }
        }
    }, [props.townships]);

    useEffect(() => {
        if (props.expandAll === true || props.selectAll === true) {
            expandAllItems();
            setSelectAll(props.selectAll);
        } else {
            // Trigger checkAllSelect and then turn off.
            setSelectAll(true);
            setExpandedTracts([]);
            setExpandedFields(props.landSystemId === LandSystem.EastTexas ? [1] : []);
            setExpandedTownships([]);
        }
    }, [props.expandAll, props.selectAll]);

    useEffect(() => {
        let sHorizons = [...props.selectedHorizons];

        if (selectAll === true && expandedTracts.length > 0) {
            let tractList: ReportTractDto[] = [];
            for (let t of props.townships) {
                for (let fl of t.fieldList) {
                    tractList = tractList.concat(fl.tractList);
                }
            }
            for (let t of tractList) {
                let result = checkHorizonSelect(t, t.horizonList, sHorizons);
                sHorizons = result.sHorizons;
            }
        } else if (toggleSelect && expandedTracts.length > 0) {
            let currTract: ReportTractDto;
            for (let t of props.townships) {
                for (let fl of t.fieldList) {
                    let tract = fl.tractList.find(tl => tl.id === expandedTracts[expandedTracts.length - 1]);
                    if (!!tract) {
                        currTract = tract;
                        break;
                    }
                }
            }
            let result = checkHorizonSelect(currTract, currTract.horizonList, sHorizons);
            sHorizons = result.sHorizons;
            setToggleSelect(false);
        }

        if (selectAll === true) {
            checkAllSelect();
        } else {
            props.setSelectedHorizons(sHorizons);
        }
    }, [expandedTracts]);

    useEffect(() => {
        if (expandedFields.length > 0) {
            let dlcNumber: number;
            let childTracts: ReportTractDto[];

            if (toggleSelect && dlcNumber > 0) {
                let currSection;
                for (let t of props.townships) {
                    let sec = t.fieldList.find(fl => fl.fieldNumber === expandedFields[expandedFields.length - 1]);
                    if (!!sec) {
                        currSection = sec;
                        break;
                    }
                }
                if (!!currSection) {
                    childTracts = currSection.tractList;
                    let result = checkTractSelect(dlcNumber, childTracts, [...props.selectedHorizons]);
    
                    props.setSelectedHorizons(result.sHorizons);
                }
            }
        }
    }, [expandedFields]);

    useEffect(() => {
        if (toggleSelect && expandedTownships.length > 0) {
            if (props.townships?.length > 0) {
                let lastJTownship = props.townships.find((t) => t.id === expandedTownships[expandedTownships.length - 1]);
                let tSections = lastJTownship.fieldList;
                let result = checkSectionSelect(lastJTownship.id, tSections, [...props.selectedHorizons]);
                props.setSelectedHorizons(result.sHorizons);
            }
        }
    }, [expandedTownships]);

    const expandAllItems = () => {
        let newExpandedTownships = [];
        let newExpandedFields = [];
        let newExpandedTracts = [];
        
        for (let t of props.townships) {
            newExpandedTownships.push(t.id);
            for (let s of t.fieldList) {
                newExpandedFields.push(s.globalNumber);
                for (let tr of s.tractList) {
                    newExpandedTracts.push(tr.id);
                }
            }
        }

        if (props.landSystemId !== LandSystem.EastTexas) {
            setExpandedTownships(newExpandedTownships);
        }
        setExpandedFields(newExpandedFields);
        setExpandedTracts(newExpandedTracts);
    };

    const checkAllSelect = () => {
        let currentForm = props.getValues();

        for (const [key] of Object.entries(currentForm)) {
            let isButton = key.length > 2 && key.substring(0, 3) === 'but';
            if (!isButton) {
                props.setValue(key, props.selectAll === true);
            }
        }
        
        let allSummaries: ReportHorizonDto[] = [];
        props.townships.map(t => {
            return t.fieldList.map(fl => {
                return fl.tractList.map(tl => {
                    allSummaries = allSummaries.concat(tl.horizonList);
                });
            });
        });

        props.setSelectedHorizons(allSummaries);
        setSelectAll(false);
    };

    const checkSectionSelect = (townshipId: number, tSections: ReportFieldDto[], sHorizons: ReportHorizonDto[]): {isSelected: boolean, sHorizons: ReportHorizonDto[]} => {
        let currentForm = props.getValues();
        let currTownship = props.townships?.find(t => t.id === townshipId);
        let tSelected = currentForm[currTownship.townshipName];

        for (let section of tSections) {
            let sectionGlobal = `${appContext.clientNumber},${appContext.projectNumber},${section.globalNumber}`;
            let sTracts = section.tractList;
            if (tSelected === true) {
                props.setValue(sectionGlobal, true);
            } else {
                props.setValue(sectionGlobal, false);
            }
            let result = checkTractSelect(section.globalNumber, sTracts, sHorizons);
            sHorizons = result.sHorizons;
        }

        return { isSelected: tSelected, sHorizons: sHorizons };
    };

    const checkTractSelect = (fieldNumber: number, sTracts: ReportTractDto[], sHorizons: ReportHorizonDto[]): {isSelected: boolean, sHorizons: ReportHorizonDto[] } => {
        let currentForm = props.getValues();

        let sectionGlobal = `${appContext.clientNumber},${appContext.projectNumber},${fieldNumber}`;
        let sectionSelected = currentForm[sectionGlobal];
        
        for (let tract of sTracts) {
            let tractGlobal = `${appContext.clientNumber},${appContext.projectNumber},${tract.fieldGlobal},${tract.tractNumber}`;
            if (sectionSelected === true) {
                props.setValue(tractGlobal, true);
            } else {
                props.setValue(tractGlobal, false);   
            }
            let result = checkHorizonSelect(tract, tract.horizonList, sHorizons);
            sHorizons = result.sHorizons;
        }

        return { isSelected: sectionSelected, sHorizons: sHorizons };
    };

    const checkHorizonSelect = (tract: ReportTractDto, tHorizons: ReportHorizonDto[], sHorizons: ReportHorizonDto[]): {isSelected: boolean, sHorizons: ReportHorizonDto[] } => {
        let currentForm = props.getValues();
        let tractGlobal = `${appContext.clientNumber},${appContext.projectNumber},${tract.fieldGlobal},${tract.tractNumber}`;
        let tractSelected = currentForm[tractGlobal];
        
        for (let horizon of tHorizons) {
            let hGlobal = horizon.globalId.split('.');
            let horizonGlobal = hGlobal.join(',');
            horizonGlobal = horizonGlobal.replace('-', ',');
            if (tractSelected === true && sHorizons.indexOf(horizon) === -1) {
                props.setValue(horizonGlobal, true);
                sHorizons.push(horizon);
            } else if (tractSelected !== true && sHorizons.indexOf(horizon) > -1) {
                props.setValue(horizonGlobal, false);
                sHorizons.splice(sHorizons.indexOf(horizon), 1);
            }
        }

        return { isSelected: tractSelected, sHorizons: sHorizons };
    };
    
    const displayTownships = () => {
        if (props.townships?.length > 0) {
            return (
                <div>
                    {props.townships.map((township: ReportTownshipDto, index: number) => {
                        let tNumber = township.townshipName;

                        return (
                            <div key={index} style={{ borderBottom: '1px solid grey' }}>
                                <div style={{ display: 'flex' }}>
                                    <div className="clickable" onClick={() => handleExpandTownships(township.id)}>
                                        +
                                    </div>
                                    <div>
                                        <input
                                            name={tNumber}
                                            id={tNumber}
                                            type="checkbox"
                                            onChange={() => handleTownshipSelect(township.id)}
                                            ref={props.register}
                                            disabled={props.loadingFields}
                                        />
                                    </div>

                                    {tNumber}
                                </div>
                                <div>{displaySections(township)}</div>
                            </div>
                        );
                    })}
                </div>
            );
        } else {
            return null;
        }
    };

    const displaySections = (township: ReportTownshipDto) => {
        const currentSections = township.fieldList;

        if (currentSections?.length > 0) {
            return currentSections.map((s: ReportFieldDto, index: number) => {
                // NOTE: Using commas here, so the form doesn't treat it like an array.
                let sectionGlobal = `${appContext.clientNumber},${appContext.projectNumber},${s.globalNumber}`;
                let isEastern = props.landSystemId === LandSystem.Eastern;

                return (
                    <div hidden={!expandedTownships.includes(township.id)} key={index} style={{ marginLeft: '1rem' }}>
                        <div style={{ display: 'flex' }}>
                            <div className="clickable" onClick={() => handleExpandFields(s.globalNumber)}>
                                +
                            </div>
                            <div>
                                <input
                                    name={sectionGlobal}
                                    id={`chkSection${s.id}`}
                                    type="checkbox"
                                    ref={props.register}
                                    onChange={() => handleSectionSelect(s)}
                                    disabled={props.loadingFields}
                                />
                            </div>
                            {`${isEastern ? 'Map' : 'Section'} ${s.fieldNumber} (${s.globalNumber})`}
                        </div>
                        <div>{displayTracts(s)}</div>
                    </div>
                );
            });
        } else {
            return null;
        }
    };

    const displayTracts = (section: ReportFieldDto) => {
        const currentTracts = section.tractList;

        if (currentTracts?.length > 0) {
            return currentTracts.map((t: ReportTractDto, index: number) => {
                let tractDetailDisplay = `Tract ${t.tractNumber} - ${t?.horizonList[0]?.grossAcres} Acres`;
                let tractGlobal = `${appContext.clientNumber},${appContext.projectNumber},${t.fieldGlobal},${t.tractNumber}`;

                return (
                    <div hidden={!expandedFields.includes(section.globalNumber)} key={index} style={{ marginLeft: '1rem' }}>
                        <div style={{ display: 'flex' }}>
                            <div className="clickable" onClick={() => handleExpandTracts(t.id)}>
                                +
                            </div>
                            <div>
                                <input
                                    name={tractGlobal}
                                    id={`chkTract${t.id}`}
                                    type="checkbox"
                                    ref={props.register}
                                    onChange={() => handleTractSelect(t)}
                                />
                            </div>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tractDetailDisplay}</div>
                        </div>
                        <div>{displayHorizons(t.id, t.horizonList)}</div>
                    </div>
                );
            });
        } else {
            return null;
        }
    };

    const displayHorizons = (tractId: number, horizons: ReportHorizonDto[]) => {
        if (horizons?.length > 0) {
            return horizons.map((h: ReportHorizonDto, index: number) => {
                // Updating global format for form consistency.
                // Note: Adding replaceAll to tsconfig is probably more efficient than this.
                let hGlobal = h.globalId.split('.');
                let horizonGlobal = hGlobal.join(',');
                horizonGlobal = horizonGlobal.replace('-', ',');

                let horizonDetailDisplay = `Horizon ${h.horizonNumber}`;
                if (!!h.from && !!h.to) {
                    let interval = HorizonInterval.fromSummary(h);
                    interval.to.calcType();
                    interval.from.calcType();
                    horizonDetailDisplay += ' - From ' + interval.from.toString() + ' To ' + interval.to.toString();
                }

                return (
                    <div hidden={!expandedTracts.includes(tractId)} key={index} style={{ marginLeft: '2rem' }}>
                        <div style={{ display: 'flex' }}>
                            <div>
                                <input
                                    name={horizonGlobal}
                                    id={`chkHorizon${h.id}`}
                                    type="checkbox"
                                    ref={props.register}
                                    onChange={() => updateHorizonSelect(h)}
                                />
                            </div>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{horizonDetailDisplay}</div>
                        </div>
                    </div>
                );
            });
        } else {
            return <div>No Horizons</div>;
        }
    };

    const handleExpandTownships = (townshipId: number) => {
        if (!props.loadingFields) {
            let newExpanded = [...expandedTownships];
    
            if (expandedTownships.includes(townshipId)) {
                setExpandedTownships(newExpanded.filter((s) => s !== townshipId));
            } else {
                newExpanded.push(townshipId);
                setExpandedTownships(newExpanded);
            }
        }
    };

    const handleExpandFields = (sectionNumber: number) => {
        if (!props.loadingFields) {
            let newExpanded = [...expandedFields];
    
            if (expandedFields.includes(sectionNumber)) {
                setExpandedFields(newExpanded.filter((s) => s !== sectionNumber));
            } else {
                newExpanded.push(sectionNumber);
                setExpandedFields(newExpanded);
            }
        }
    };

    const handleExpandTracts = (tractId: number) => {
        let newExpanded = [...expandedTracts];

        if (expandedTracts.includes(tractId)) {
            setExpandedTracts(newExpanded.filter((t) => t !== tractId));
        } else {
            newExpanded.push(tractId);
            setExpandedTracts(newExpanded);
        }
    };

    const handleTownshipSelect = (townshipId: number) => {
        setToggleSelect(true);
        let tSections = props.townships.find(t => t.id === townshipId).fieldList;

        let result = checkSectionSelect(townshipId, tSections, [...props.selectedHorizons]);
        props.setSelectedHorizons(result.sHorizons);

        let included = expandedTownships.includes(townshipId);
        if (included !== result.isSelected) {
            handleExpandTownships(townshipId);
        }
    };

    const handleSectionSelect = (section: ReportFieldDto) => {
        setToggleSelect(true);

        let sTracts = section.tractList;

        let result = checkTractSelect(section.globalNumber, sTracts, [...props.selectedHorizons]);
        props.setSelectedHorizons(result.sHorizons);

        let included = expandedFields.includes(section.globalNumber);
        if (included !== result.isSelected) {
            handleExpandFields(section.globalNumber);
        }
    };

    const handleTractSelect = (tract: ReportTractDto) => {
        setToggleSelect(true);

        let result = checkHorizonSelect(tract, tract.horizonList, [...props.selectedHorizons]);
        props.setSelectedHorizons(result.sHorizons);

        let included = expandedTracts.includes(tract.id);
        if (included !== result.isSelected) {
            handleExpandTracts(tract.id);
        }
    };

    const updateHorizonSelect = (horizonIntervalId: ReportHorizonDto) => {
        let selected = [...props.selectedHorizons];

        if (props.selectedHorizons.includes(horizonIntervalId)) {
            selected.splice(selected.indexOf(horizonIntervalId), 1);
        } else {
            selected.push(horizonIntervalId);
        }

        props.setSelectedHorizons(selected);
    };

    return (
        <div>
            {props.loadingFields && (
                <>
                    <LoadingSpinner />
                    <div>Loading Fields</div>
                </>
            )}
            {props.landSystemId === LandSystem.EastTexas && (
                <>
                    {props.townships?.length > 0 && props.townships[0].fieldList?.length > 0 && (
                        displayTracts(props.townships[0].fieldList[0])
                    )}
                    {!props.townships || props.townships?.length === 0 && (
                        <span>Loading...</span>
                    )}
                </>
            )}
            {(props.landSystemId !== LandSystem.EastTexas) && (
                displayTownships()
            )}
        </div>
    );
};

export default ReportFilterLocation;



// WEBPACK FOOTER //
// ./src/components/project/sections/ReportComponents/ReportFilterLocation.tsx